import React, { createContext, useCallback, useContext } from 'react';
import { driver, DriveStep } from 'driver.js';
import { useLocation } from 'react-router-dom';

/* ── Context ── */

type TourContextType = { startTour: () => void };
const TourContext = createContext<TourContextType>({ startTour: () => {} });
export const useTour = () => useContext(TourContext);

/* ── Shared navbar steps (shown on every page) ── */

const NAV_STEPS: DriveStep[] = [
  {
    element: '#tour-logo',
    popover: {
      title: 'A-Vertex Platform',
      description:
        'Welcome to A-Vertex DevHub! This is your central developer portal. Click the logo to return to the Project Estimator.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '#tour-nav-devhub-btn',
    popover: {
      title: 'Development Hub Navigation',
      description:
        'Toggle this button to expand or collapse the DevHub section, revealing all module links in the pill bar.',
      side: 'bottom',
    },
  },
  {
    element: '#tour-nav-home',
    popover: {
      title: 'Home — AI Assistant',
      description:
        'The Home page features ASC AI, an intelligent assistant for managing apps, running deployments, and exploring the platform — just type naturally.',
      side: 'bottom',
    },
  },
  {
    element: '#tour-nav-catalog',
    popover: {
      title: 'Catalog',
      description:
        'Browse all registered software components, systems, APIs, and teams in the Backstage catalog.',
      side: 'bottom',
    },
  },
  // {
  //   element: '#tour-nav-apis',
  //   popover: {
  //     title: 'APIs',
  //     description:
  //       'Explore and test all registered API definitions — REST, GraphQL, and gRPC — with interactive documentation.',
  //     side: 'bottom',
  //   },
  // },
  {
    element: '#tour-nav-docs',
    popover: {
      title: 'Docs (TechDocs)',
      description:
        'Access technical documentation for all services. Docs are built automatically from source code repositories.',
      side: 'bottom',
    },
  },
  {
    element: '#tour-nav-devkits',
    popover: {
      title: 'DevKits',
      description:
        'Scaffold new projects from standardised templates. DevKits pre-configure repos with best-practice structures for Python, Node.js, infrastructure, and more.',
      side: 'bottom',
    },
  },
  {
    element: '#tour-nav-requests',
    popover: {
      title: 'Requests',
      description:
        'Submit infrastructure and cloud resource requests — AWS account setup, access grants, automation tasks — all tracked in one place.',
      side: 'bottom',
    },
  },
  {
    element: '#tour-nav-deployments',
    popover: {
      title: 'Deployments',
      description:
        'Track and trigger application deployments across all your environments, directly integrated with GitHub Actions workflows.',
      side: 'bottom',
    },
  },
  {
    element: '#tour-nav-cost-explorer',
    popover: {
      title: 'Cost Explorer',
      description:
        'Monitor and analyse your AWS cloud spend with detailed cost breakdowns by project, service, and time period.',
      side: 'bottom',
    },
  },
  // {
  //   element: '#tour-nav-autogendoc',
  //   popover: {
  //     title: 'AutoGenDoc',
  //     description:
  //       'Use AI to automatically generate and publish technical documentation from your source code and APIs.',
  //     side: 'bottom',
  //   },
  // },
  {
    element: '#tour-nav-radar',
    popover: {
      title: 'Tech Radar',
      description:
        "Visualise your organisation's technology adoption standards — what's recommended, on hold, or being phased out.",
      side: 'bottom',
    },
  },
  {
    element: '#tour-notifications',
    popover: {
      title: 'Notifications',
      description:
        'Stay up to date with platform alerts, pipeline completions, and system events.',
      side: 'bottom',
      align: 'end',
    },
  },
  {
    element: '#tour-profile',
    popover: {
      title: 'Profile & Settings',
      description:
        'Manage your account settings, linked providers (GitHub, Azure), and user preferences.',
      side: 'bottom',
      align: 'end',
    },
  },
];

/* ── Page-specific steps ── */

const PAGE_STEPS: Record<string, DriveStep[]> = {
  '/': [
    {
      element: '#tour-home-header',
      popover: {
        title: 'Welcome to DevHub',
        description:
          'This sticky header gives you context for the current section. On the Home page you have direct access to the full AI assistant.',
        side: 'bottom',
      },
    },
    {
      element: '#tour-ai-chat',
      popover: {
        title: 'ASC AI Assistant',
        description:
          'Your intelligent platform assistant. Type naturally to create projects, deploy apps, query services, or get help with the platform.',
        side: 'right',
      },
    },
    {
      element: '#tour-chat-input',
      popover: {
        title: 'Chat Input',
        description:
          'Type your request here. Try: "Create a new Python microservice", "Deploy my repo to staging", or "Show all catalog components".',
        side: 'top',
      },
    },
    {
      element: '#tour-stats-cards',
      popover: {
        title: 'Platform Stats',
        description:
          'At-a-glance metrics — total projects, models, docs, and your last 30 days of cloud spend. Click any card to explore further.',
        side: 'left',
      },
    },
    {
      element: '#tour-tools-section',
      popover: {
        title: 'Platform Tools',
        description:
          'Quick-launch shortcuts to your cloud platforms — AWS, Azure, Databricks, and GitHub. Click any card to open that service.',
        side: 'left',
      },
    },
    {
      element: '#tour-starred-entities',
      popover: {
        title: 'Starred Entities',
        description:
          'Your bookmarked catalog items appear here. Star any component, API, or resource in the Catalog to pin it for quick access.',
        side: 'left',
      },
    },
  ],
  '/catalog': [
    {
      element: 'main',
      popover: {
        title: 'Software Catalog',
        description:
          'All registered software entities live here. Use the filters on the left to narrow by kind (Component, API, System, Group, User) and type.',
        side: 'right',
      },
    },
  ],
  // '/api-docs': [
  //   {
  //     element: 'main',
  //     popover: {
  //       title: 'API Explorer',
  //       description:
  //         'Browse every registered API with its spec, owner, and interactive docs. Filter by API type (REST, GraphQL, gRPC) using the left panel.',
  //       side: 'right',
  //     },
  //   },
  // ],
  '/docs': [
    {
      element: 'main',
      popover: {
        title: 'TechDocs',
        description:
          'Technical documentation for all services, automatically built from Markdown sources stored alongside the code in each repository.',
        side: 'right',
      },
    },
  ],
  '/devkits': [
    {
      element: 'main',
      popover: {
        title: 'DevKits — Project Templates',
        description:
          'Choose a DevKit template to scaffold a new repository. Each kit is pre-configured with CI/CD, linting, and project structure best practices.',
        side: 'right',
      },
    },
  ],
  '/requests': [
    {
      element: 'main',
      popover: {
        title: 'Cloud Requests',
        description:
          'Submit and track infrastructure requests such as new AWS accounts, IAM roles, or resource provisioning tasks.',
        side: 'right',
      },
    },
  ],
  '/deployments': [
    {
      element: 'main',
      popover: {
        title: 'Deployments',
        description:
          'Monitor application deployment pipelines and trigger GitHub Actions workflows directly from this page.',
        side: 'right',
      },
    },
  ],
  '/cost-explorer': [
    {
      element: 'main',
      popover: {
        title: 'Cost Explorer',
        description:
          'Analyse AWS cloud spending broken down by service, project, or date range. Use the filters to drill into specific cost drivers.',
        side: 'right',
      },
    },
  ],
  // '/auto-gen-doc': [
  //   {
  //     element: 'main',
  //     popover: {
  //       title: 'AutoGenDoc',
  //       description:
  //         'Automatically generate documentation from your source code and APIs using AI. Select a repository and click Generate to start.',
  //       side: 'right',
  //     },
  //   },
  // ],
  '/tech-radar': [
    {
      element: 'main',
      popover: {
        title: 'Tech Radar',
        description:
          "An interactive view of your organisation's technology standards. Hover over any blip to learn about its adoption status and rationale.",
        side: 'right',
      },
    },
  ],
};

/* ── Provider ── */

export function TourProvider({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();

  const startTour = useCallback(() => {
    const pageKey = Object.keys(PAGE_STEPS).find(k =>
      k === '/' ? pathname === '/' : pathname.startsWith(k),
    );
    const pageSpecific = pageKey ? PAGE_STEPS[pageKey] : [];

    const baseSteps = pathname === '/' ? [...NAV_STEPS, ...pageSpecific] : [...pageSpecific];

    // Only include steps whose target element is present in the DOM
    const steps = baseSteps.filter(step => {
      if (!step.element || typeof step.element !== 'string') return true;
      return !!document.querySelector(step.element);
    });

    if (steps.length === 0) return;

    const driverObj = driver({
      showProgress: true,
      animate: true,
      smoothScroll: true,
      overlayColor: 'rgba(0, 0, 0, 0.45)',
      stagePadding: 6,
      stageRadius: 10,
      popoverClass: 'devhub-tour-popover',
      progressText: 'Step {{current}} of {{total}}',
      nextBtnText: 'Next →',
      prevBtnText: '← Back',
      doneBtnText: '✓ Done',
      steps,
    });

    driverObj.drive();
  }, [pathname]);

  return (
    <TourContext.Provider value={{ startTour }}>{children}</TourContext.Provider>
  );
}
